import { useEffect, useMemo, useState } from "react";
import {
  BookOpenCheck,
  Building2,
  GraduationCap,
  Layers3,
  LogOut,
  Plus,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

const ORG_TYPES = [
  ["school", "School"],
  ["college", "College"],
  ["training_provider", "Training provider"],
  ["rail_operator", "Rail operator"],
  ["heritage_railway", "Heritage railway"],
  ["employer", "Employer"],
  ["other", "Other"],
];

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function LMSPlatformAdmin() {
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    organisation_type: "school",
    reference: "",
  });

  const [slugEdited, setSlugEdited] = useState(false);

  const load = async () => {
    setError("");

    try {
      const me = await lmsApi.get("/auth/me");

      if (me.data.require_password_change) {
        navigate("/learn/change-password", {
          replace: true,
        });
        return;
      }

      if (!me.data.is_platform_admin) {
        const orgMembership = me.data.memberships?.find((item) =>
          ["organisation_admin", "instructor", "auditor"].includes(item.role)
        );

        navigate(
          orgMembership
            ? `/learn/organisations/${orgMembership.organisation_id}`
            : "/learn",
          {
            replace: true,
          }
        );

        return;
      }

      const [summaryResponse, organisationsResponse] = await Promise.all([
        lmsApi.get("/platform/summary"),
        lmsApi.get("/platform/organisations"),
      ]);

      setSummary(summaryResponse.data);
      setOrganisations(organisationsResponse.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/learn/login", {
          replace: true,
        });
        return;
      }

      setError(
        err.response?.data?.detail ||
          "We could not load TRAC-Learn administration."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCount = useMemo(
    () =>
      organisations.filter(
        (organisation) => organisation.status === "active"
      ).length,
    [organisations]
  );

  const handleNameChange = (value) => {
    setForm((current) => ({
      ...current,
      name: value,
      slug: slugEdited ? current.slug : slugify(value),
    }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    setSubmitting(true);
    setError("");

    try {
      const response = await lmsApi.post("/platform/organisations", {
        name: form.name,
        slug: form.slug,
        organisation_type: form.organisation_type,
        reference: form.reference || null,
      });

      setShowCreate(false);

      setForm({
        name: "",
        slug: "",
        organisation_type: "school",
        reference: "",
      });

      setSlugEdited(false);

      navigate(
        `/learn/admin/organisations/${response.data.id}`
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The organisation could not be created."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await lmsApi.post("/auth/logout");
    } finally {
      navigate("/learn/login", {
        replace: true,
      });
    }
  };

  if (loading) {
    return (
      <section className="min-h-[60vh] bg-slate-50 py-20">
        <div className="container-rail text-center text-slate-600">
          Loading TRAC-Learn administration…
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title="TRAC-Learn Platform Administration | UKTDA"
        description="Secure UKTDA administration for TRAC-Learn organisations."
        path="/learn/admin"
      />

      <section className="bg-navy py-10 text-white md:py-14">
        <div className="container-rail flex flex-col justify-between gap-6 md:flex-row md:items-center">
          <div>
            <div className="flex items-center gap-2 text-safety">
              <ShieldCheck className="h-5 w-5" />

              <p className="label-eyebrow">
                UKTDA Super Admin
              </p>
            </div>

            <h1 className="mt-3 font-display text-4xl font-bold md:text-5xl">
              TRAC-Learn
            </h1>

            <p className="mt-3 max-w-2xl text-white/70">
              Create and control organisations, build the UKTDA course
              catalogue, then grant learning into each tenant workspace.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() =>
                navigate("/learn/admin/courses")
              }
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/10"
            >
              <BookOpenCheck className="h-4 w-4" />
              Manage courses
            </button>

            <button
              type="button"
              onClick={() =>
                setShowCreate((value) => !value)
              }
              className="inline-flex items-center gap-2 rounded-lg bg-safety px-4 py-3 text-sm font-bold text-white transition hover:bg-safety/90"
            >
              <Plus className="h-4 w-4" />
              New organisation
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-10 md:py-14">
        <div className="container-rail">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {[
              [
                Building2,
                "Organisations",
                summary?.organisation_count ??
                  organisations.length,
              ],
              [
                ShieldCheck,
                "Active organisations",
                summary?.active_organisation_count ??
                  activeCount,
              ],
              [
                Users,
                "Learners",
                summary?.learner_count ?? 0,
              ],
              [
                Layers3,
                "Enrolments",
                summary?.enrolment_count ?? 0,
              ],
              [
                BookOpenCheck,
                "Courses",
                summary?.course_count ?? 0,
              ],
            ].map(([Icon, label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-500">
                  <Icon className="h-5 w-5 text-safety" />
                  {label}
                </div>

                <p className="mt-3 font-display text-3xl font-bold text-navy">
                  {value}
                </p>
              </div>
            ))}
          </div>

          {showCreate && (
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
              <div className="max-w-3xl">
                <p className="label-eyebrow text-safety">
                  Organisation gate
                </p>

                <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                  Create an organisation
                </h2>

                <p className="mt-3 text-slate-600">
                  Creating the organisation establishes its isolated
                  TRAC-Learn workspace. Administrators, cohorts, learners and
                  course access can then be configured inside it.
                </p>
              </div>

              <form
                onSubmit={handleCreate}
                className="mt-7 grid gap-5 md:grid-cols-2"
              >
                <label className="text-sm font-semibold text-navy md:col-span-2">
                  Organisation name

                  <input
                    value={form.name}
                    onChange={(event) =>
                      handleNameChange(event.target.value)
                    }
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety focus:ring-2 focus:ring-safety/20"
                    placeholder="Example Academy Trust"
                  />
                </label>

                <label className="text-sm font-semibold text-navy">
                  Organisation type

                  <select
                    value={form.organisation_type}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        organisation_type:
                          event.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 outline-none focus:border-safety"
                  >
                    {ORG_TYPES.map(([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-sm font-semibold text-navy">
                  Customer/reference code{" "}
                  <span className="font-normal text-slate-400">
                    optional
                  </span>

                  <input
                    value={form.reference}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        reference:
                          event.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                    placeholder="CRM or contract reference"
                  />
                </label>

                <label className="text-sm font-semibold text-navy md:col-span-2">
                  Workspace slug

                  <input
                    value={form.slug}
                    onChange={(event) => {
                      setSlugEdited(true);

                      setForm((current) => ({
                        ...current,
                        slug: slugify(
                          event.target.value
                        ),
                      }));
                    }}
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 font-mono text-sm outline-none focus:border-safety"
                    placeholder="example-academy-trust"
                  />

                  <span className="mt-1 block text-xs font-normal text-slate-500">
                    Permanent internal identifier used to separate the
                    organisation workspace.
                  </span>
                </label>

                <div className="flex gap-3 md:col-span-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg bg-navy px-5 py-3 text-sm font-bold text-white disabled:opacity-60"
                  >
                    {submitting
                      ? "Creating…"
                      : "Create organisation"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setShowCreate(false)
                    }
                    className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="mt-10 flex items-end justify-between gap-4">
            <div>
              <p className="label-eyebrow text-safety">
                Organisation control
              </p>

              <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                Organisations
              </h2>
            </div>

            <p className="hidden text-sm text-slate-500 md:block">
              Only UKTDA Super Admin can create this top-level access gate.
            </p>
          </div>

          {organisations.length ? (
            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-4">
                        Organisation
                      </th>

                      <th className="px-5 py-4">
                        Type
                      </th>

                      <th className="px-5 py-4">
                        Learners
                      </th>

                      <th className="px-5 py-4">
                        Cohorts
                      </th>

                      <th className="px-5 py-4">
                        Enrolments
                      </th>

                      <th className="px-5 py-4">
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {organisations.map(
                      (organisation) => (
                        <tr
                          key={organisation.id}
                          onClick={() =>
                            navigate(
                              `/learn/admin/organisations/${organisation.id}`
                            )
                          }
                          className="cursor-pointer transition hover:bg-slate-50"
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy text-white">
                                <GraduationCap className="h-5 w-5" />
                              </div>

                              <div>
                                <p className="font-bold text-navy">
                                  {organisation.name}
                                </p>

                                <p className="font-mono text-xs text-slate-400">
                                  {organisation.slug}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-4 capitalize text-slate-600">
                            {organisation.organisation_type.replaceAll(
                              "_",
                              " "
                            )}
                          </td>

                          <td className="px-5 py-4 font-semibold text-navy">
                            {organisation.learner_count}
                          </td>

                          <td className="px-5 py-4 font-semibold text-navy">
                            {organisation.cohort_count}
                          </td>

                          <td className="px-5 py-4 font-semibold text-navy">
                            {organisation.enrolment_count}
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                                organisation.status ===
                                "active"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : organisation.status ===
                                    "suspended"
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {organisation.status}
                            </span>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
              <Building2 className="mx-auto h-10 w-10 text-slate-400" />

              <h3 className="mt-4 font-display text-xl font-bold text-navy">
                No organisations yet
              </h3>

              <p className="mt-2 text-slate-600">
                Create the first organisation to open its TRAC-Learn
                workspace.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}